<template>
  <section>
    <generic-modal title="Taalbestand importeren" :error="allErrors">
      <template #content>
        <div class="content">
          <div class="field">
            <label>Bestand</label>
            <label for="file" class="upload">Selecteer bestand</label>
            <input
              type="file"
              id="file"
              ref="file"
              @input="changeFile"
              accept=".json,.xls,.xlsx,.csv"
            />
            <span class="infofield">{{ filename }}</span>
            <font-awesome-icon
              v-if="filename"
              icon="times"
              class="remove"
              @click="clearFile"
            />
          </div>
          <template v-if="isExcel && fileInfo">
            <div class="field">
              <label for="sheet">Sheet</label>
              <select id="sheet" v-model="sheet">
                <option v-for="(name, i) in sheets" :key="i" :value="i">
                  ({{ i }}) {{ name }}
                </option>
              </select>
            </div>
            <div class="field">
              <label for="languages">Talen</label>
              <select multiple id="languages" v-model="importLanguages">
                <option
                  v-for="lang in languages"
                  :key="lang.value"
                  :value="lang.value"
                >
                  {{ lang.label }} ({{ lang.value }})
                </option>
              </select>
            </div>
          </template>
          <div v-if="importMessage" class="success">
            {{ importMessage }}
          </div>
        </div>
      </template>
      <template #action-buttons>
        <button
          class="action btn btn-dark"
          @click="confirm"
          :disabled="!canImport"
        >
          <font-awesome-icon v-if="isLoading" icon="spinner" />
          <span v-else>Importeren</span>
        </button>
        <button
          :disabled="isLoading"
          class="action btn btn-light"
          @click="cancel"
        >
          Annuleren
        </button>
      </template>
    </generic-modal>
  </section>
</template>

<script>
import GenericModal from '@/components/elements/GenericModal.vue';
import {
  importQuestionnaireLanguage,
  importQuestionnaireLanguageExcel,
  importTemplateLanguage,
  importTemplateLanguageExcel,
  preflightExcelFile,
} from '@/api';

function isExcel(filename) {
  filename = filename.toLowerCase();
  return (
    filename.endsWith('.xls') ||
    filename.endsWith('.xlsx') ||
    filename.endsWith('.csv')
  );
}

export default {
  props: ['resource', 'config', 'language', 'version'],
  components: { GenericModal },
  data() {
    return {
      isLoading: false,
      filename: null,
      fileContents: null,
      error: null,
      fileInfo: null,
      importMessage: null,

      // Sheet to import if an Excel file is selected
      sheet: 0,

      // Languages to import if an Excel file is selected
      importLanguages: [],
    };
  },
  methods: {
    changeFile() {
      const file = this.$refs.file.files[0];

      if (!file) {
        this.clearFile();
        return;
      }

      const reader = new FileReader();
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.json')) {
        // Read as readable text
        reader.addEventListener('load', () => {
          this.filename = file.name;
          this.fileContents = reader.result;
          this.fileInfo = null;
          this.preflight();
        });
        reader.readAsText(file);
      } else if (isExcel(fileName)) {
        // Read base64 encoded data URI
        reader.addEventListener('load', () => {
          this.filename = file.name;

          // Take the base64 contents off the data URI
          this.fileContents = reader.result.split(',')[1];
          this.preflight();
        });
        reader.readAsDataURL(file);
      } else {
        this.clearFile();
      }
    },
    async preflight() {
      if (!this.filename) {
        return;
      }

      if (isExcel(this.filename)) {
        this.fileInfo = await preflightExcelFile(
          this.version,
          this.fileContents
        );
      } else {
        this.fileInfo = {
          languages: [this.language],
        };
      }
    },
    clearFile() {
      this.error = null;
      this.filename = null;
      this.fileContents = null;
      this.fileInfo = null;
      this.importMessage = null;
      this.$refs.file.value = '';
      this.sheet = 0;
      this.importLanguages = [];
    },
    async confirm() {
      this.error = null;
      this.isLoading = true;
      try {
        await this.doImport();
      } catch (error) {
        this.error = error.response?.data?.message || 'Er is iets misgegaan';
      } finally {
        this.isLoading = false;
      }
    },
    async doImport() {
      this.importMessage = null;

      if (this.resource === 'templates') {
        if (isExcel(this.filename)) {
          await importTemplateLanguageExcel(
            this.config,
            this.version,
            this.filename,
            this.fileContents,
            this.sheet,
            this.importLanguages
          );
        } else {
          await importTemplateLanguage(
            this.config,
            this.version,
            this.fileContents,
            this.language
          );
        }
      } else if (this.resource === 'questionnaire') {
        if (isExcel(this.filename)) {
          await importQuestionnaireLanguageExcel(
            this.config,
            this.version,
            this.filename,
            this.fileContents,
            this.sheet,
            this.importLanguages
          );
        } else {
          await importQuestionnaireLanguage(
            this.config,
            this.version,
            this.fileContents,
            this.language
          );
        }
      }

      this.importMessage = 'Import voltooid.';
    },
    cancel() {
      this.$emit('cancel');
    },
  },
  computed: {
    isExcel() {
      return this.filename && isExcel(this.filename);
    },
    sheets() {
      if (!this.fileInfo) {
        return [];
      }

      return this.fileInfo.map((sheet) => sheet.title);
    },

    // Languages available in the current sheet
    languages() {
      if (!this.fileInfo) {
        return [];
      }

      return this.fileInfo[this.sheet].languages;
    },

    canImport() {
      if (this.isLoading || !this.filename) {
        return false;
      }

      return (
        !isExcel(this.filename) ||
        (this.fileInfo && this.importLanguages.length && this.sheet !== null)
      );
    },

    allErrors() {
      if (this.error) {
        return this.error;
      }

      if (this.fileInfo && !this.fileInfo[this.sheet].has_keys_column) {
        return 'This sheet is missing the required "keys" column.';
      }

      return null;
    },
  },
};
</script>

<style scoped lang="scss">
.field {
  display: flex;
  flex-direction: row;
  margin: 5px 0 10px 0;
  font-size: 14px;
}

label:not(.upload) {
  text-align: right;
  margin: 6px 15px 0 0;
  width: 120px;
  color: grey;
  height: 24px;
}

label.upload {
  padding: 7px 14px;
  font-size: 14px;
  border-radius: 3px;
  white-space: nowrap;
  user-select: none;
  border: 1px solid #6a103a;
  background-color: white;
  color: #6a103a;
  cursor: pointer;
  margin-left: 10px;
  margin-right: 10px;
}

.success {
  background-color: rgb(239, 250, 245);
  color: rgb(28, 90, 62);
  padding: 0.5em;
  margin: 0 0.5em;
}

#file {
  display: none;
}

.infofield {
  margin: 6px 0 6px 0;
}

.remove {
  margin: 8px;
  cursor: pointer;
}

select {
  width: 300px;
}
</style>
